import React, { useState, useEffect } from "react";
import { providerLogoSrc } from "../helpers/providerLogo";
import {
  Tabs,
  ScrollArea,
  Box,
  Button,
  Card,
  UnstyledButton,
  Avatar,
  Group,
  Text,
  Stack,
  SegmentedControl,
  TextInput,
} from "@mantine/core";
import { useServerApi } from "../hooks/userServerApi";
import { useCellRender } from "../hooks/useCellRender";
import { IconPhoto, IconBuildingSkyscraper, IconSearch } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { useAuthUser } from "react-auth-kit";
import _ from "lodash";

import ProductTab from "./productTab";
import ProviderCommissionScheduleTab from "./providerCommissionScheduleTab";
import ClientPromotionTab from "./clientPromotionTab";
import ConsultantIncentiveTab from "./consultantIncentive";
import PortalLoginTab from "./portalLoginTab";
import PaymentInfoTab from "./paymentInfoTab";
import ProviderFormTab from "./providerFormTab";
import ProviderLinkageTab from "./providerLinkageTab";
import ProviderContactTab from "./providerContactTab";

const BusinessTab = ({ mt = "xl", height = 600 }) => {
  const [activeProvider, setActiveProvider] = useState(null);
  const [activeTab, setActiveTab] = useState("products");
  const [providers, setProviders] = useState([]);
  const [clientPromotions, setClientPromotions] = useState([]);
  const [consultantIncentives, setConsultantIncentives] = useState([]);
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const { t } = useTranslation();
  const auth = useAuthUser();
  const [search, setSearch] = useState("");

  const fetchProviders = async () => {
    const res = await api.search({
      apiEntity: "provider",
      pageSize: 1000,
      sort: { by: "code", order: "asc" },
      select:
        "shortName name cname image code insurerCode tel email _id paymentInfo formFiles formLinkage isActive",

      preQuery: {
        isActive: true,
      },
      searchQuery: _.isEmpty(search)
        ? { isActive: { $ne: false } }
        : {
            $and: [
              { isActive: { $ne: false } },
              {
                $or: [
                  { shortName: { $regex: search, $options: "i" } },
                  { name: { $regex: search, $options: "i" } },
                  { cname: { $regex: search, $options: "i" } },
                ],
              },
            ],
          },
    });
    if (!res?.docs) return;
    setProviders(res.docs ?? []);
    setActiveProvider(res.docs[0] ?? null);
  };

  const fetchClientPromotions = async () => {
    const data = await api.ClientPromotion.getLatest();
    setClientPromotions(data ?? []);
  };

  const fetchConsultantIncentives = async () => {
    const data = await api.ConsultantIncentive.getLatest();
    setConsultantIncentives(data);
  };

  useEffect(() => {
    fetchProviders();
  }, [search]);

  useEffect(() => {
    fetchClientPromotions();
    fetchConsultantIncentives();
  }, []);

  const getProviderDisplayName = (provider) => {
    return provider.shortName ?? provider.code.replaceAll("-", " ");
  };

  return (
    <Box
      mt={mt}
      sx={{
        display: "grid",
        gridTemplateColumns: "300px minmax(0, 1fr)",
        gridTemplateRows: "auto minmax(0, 1fr)",
        columnGap: 16,
        rowGap: 12,
        height: "calc(100vh - 120px)",
        minHeight: 520,
      }}
    >
      <TextInput
        placeholder={t("Search providers...")}
        icon={<IconSearch size={14} />}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        sx={{ alignSelf: "start" }}
      />
      <SegmentedControl
        value={activeTab}
        fullWidth
        size="xs"
        onChange={setActiveTab}
        sx={{ alignSelf: "start", width: "100%", maxWidth: "100%", minWidth: 0 }}
        styles={{
          root: {
            width: "100%",
            maxWidth: "100%",
            flexWrap: "wrap",
            height: "auto",
          },
          control: {
            flex: "1 1 0%",
            minWidth: "max-content",
          },
          label: {
            padding: "6px 6px",
            overflow: "visible",
            textOverflow: "unset",
          },
        }}
        data={[
          { label: t("Products"), value: "products" },
          { label: t("Commission Schedule"), value: "commissionSchedule" },
          { label: t("Client Promotion"), value: "clientPromotion" },
          {
            label: t("Consultant Incentive"),
            value: "consultantIncentive",
          },
          { label: t("Contact List"), value: "contactList" },
          { label: t("Portal Login"), value: "portalLogin" },
          { label: t("Payment Information"), value: "paymentInfo" },
          { label: t("Forms"), value: "providerForm" },
          { label: t("Linkage"), value: "linkage" },
        ]}
      />

      <Box sx={{ minHeight: 0, height: "100%" }}>
        <ScrollArea h="100%" type="auto" offsetScrollbars>
          <Stack
            bg="var(--mantine-color-body)"
            align="stretch"
            justify="flex-start"
            gap="md"
          >
            {providers.map((provider) => (
              <Button
                key={provider._id}
                fullWidth
                h={40}
                variant="default"
                onClick={() => {
                  setActiveProvider(provider);
                }}
                styles={{
                  inner: { justifyContent: "flex-start" },
                  label: { width: "100%", overflow: "visible" },
                }}
                style={{
                  border:
                    activeProvider._id === provider._id
                      ? "1px solid teal"
                      : undefined,
                }}
              >
                <Group spacing={8} noWrap align="center" sx={{ width: "100%" }}>
                  <Avatar
                    src={providerLogoSrc(provider) || null}
                    radius="xl"
                    size={24}
                    sx={{ flexShrink: 0 }}
                  >
                    <IconBuildingSkyscraper size={14} />
                  </Avatar>
                  <Text
                    size="sm"
                    sx={{
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getProviderDisplayName(provider)}
                  </Text>
                </Group>
              </Button>
            ))}
          </Stack>
        </ScrollArea>
      </Box>

      <Box
        sx={{
          minHeight: 0,
          height: "100%",
          overflow: activeTab === "products" ? "hidden" : "auto",
        }}
      >
        {activeTab === "products" && (
          <ProductTab provider={activeProvider} fillHeight />
        )}
        {activeTab === "commissionSchedule" && (
          <ProviderCommissionScheduleTab
            provider={activeProvider}
            version={auth()?.isConsultant ? "CONSULTANT" : "COMPANY"}
            showGrossRateOnly={!!auth()?.isConsultant}
          />
        )}
        {activeTab === "clientPromotion" && (
          <ClientPromotionTab
            provider={activeProvider}
            clientPromotions={clientPromotions}
          />
        )}
        {activeTab === "consultantIncentive" && (
          <ConsultantIncentiveTab
            provider={activeProvider}
            consultantIncentives={consultantIncentives}
          />
        )}
        {activeTab === "contactList" && (
          <ProviderContactTab provider={activeProvider} />
        )}
        {activeTab === "portalLogin" && (
          <PortalLoginTab provider={activeProvider} />
        )}
        {activeTab === "paymentInfo" && (
          <PaymentInfoTab provider={activeProvider} />
        )}
        {activeTab === "providerForm" && (
          <ProviderFormTab provider={activeProvider} />
        )}
        {activeTab === "linkage" && (
          <ProviderLinkageTab provider={activeProvider} />
        )}
      </Box>
    </Box>
  );
};

export default BusinessTab;
